<template>
  <div class="acc-item acc-user">
    <b-dropdown aria-role="list" class="dropdown-acc-user" :mobile-modal="false">
      <template #trigger>
        <button v-if="userData" class="is-transparent" aria-haspopup="true" aria-controls="dropdown-menu">
          <span class="button-text-w">
            <b-tooltip position="is-bottom" multilined type="is-dark">
              <span class="roi-help">
                <icon-base icon-name="user"><icon-user /></icon-base>
              </span>
              <template #content>
                <div class="userinfo" v-html="userInfo"></div>
              </template>
            </b-tooltip>
            <b-tooltip position="is-bottom" multilined type="is-dark">
              <span class="text">{{ userData.Username }}</span>
              <template #content>
                <span>{{ $t("Header.Tooltips.UserProfile") }}</span>
              </template>
            </b-tooltip>
          </span>
          <icon-base width="12" height="7" view-box="0 0 12 7" icon-name="caret-down"><icon-caret-down /></icon-base>
        </button>
      </template>
      <b-dropdown-item aria-role="listitem" class="dropdown-item-link" has-link>
        <a href="/Profile/Index">
            {{ $t("Header.Profile") }}
        </a>
      </b-dropdown-item>
      <hr class="dropdown-divider" aria-role="menuitem" />
      <b-dropdown-item aria-role="listitem" class="dropdown-caption">{{ $t("Header.Language.Caption") }}</b-dropdown-item>
      <b-dropdown-item aria-role="listitem" class="dropdown-item-link" @click="changeLocale('en-US')">{{ $t("Header.Language.English") }}</b-dropdown-item>
      <b-dropdown-item aria-role="listitem" class="dropdown-item-link" @click="changeLocale('he-IL')">{{ $t("Header.Language.Hebrew") }}</b-dropdown-item>
      <hr class="dropdown-divider" aria-role="menuitem" />
      <b-dropdown-item aria-role="listitem" class="dropdown-item-link" @click="logout()">{{ $t("Header.Logout") }}</b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import i18n from "@/i18n";
import { UserData } from "@/models/UserData";
import ApiService from "@/services/api/ApiService";
import ConfigService from "@/services/ConfigService";
import HelperService from "@/services/HelperService";
import IconBase from "../IconBase.vue";
import IconUser from "../icons/IconUser.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";

const namespace = "mainModule";

@Component({
  components: {
    IconBase,
    IconUser,
    IconCaretDown,
  },
})
export default class HeaderUser extends Vue {

  //#region Vuex getters and actions

  @Getter("userData", { namespace }) userData!: UserData;
  @Action("logout", { namespace: "leadsModule" }) logoutAction!: () => void;
  @Action("setUserData", { namespace }) setUserData!: (userData: UserData | null) => void;

  //#endregion Vuex getters and actions


  //#region Getters

  get isUserLeadViewer(): boolean {
    return HelperService.isUserInRole(this.userData, ConfigService.leadViewerUserRole);
  }
  
  get userInfo(): string {
    let result: string = i18n.t('Header.TenantInfo', { TENANT_NAME: this.userData.TenantName, TENANT_ID: this.userData.TenantId }).toString();
    if (this.isUserLeadViewer) {
      result += i18n.t('Header.ClientInfo', { CLIENT_NAME: this.userData.ClientName, CLIENT_ID: this.userData.ClientId });
    }
    return result;
  }
  //#endregion Getters


  changeLocale(locale: string): void {
    this.$i18n.locale = locale;
    localStorage.locale = locale;
    ApiService.setLanguage(locale);
  }

  logout() {
    ApiService.logout().then(() => {
      this.setUserData(null);
      this.logoutAction();
      location.href = ConfigService.loginUrl;
    });
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";

.acc-item.acc-user {
  display: flex;
  align-items: stretch;
  button {
    font-weight: 600;
    direction: ltr;
    font-size: 18px;
    @media (resolution: 120dpi) {
      font-size: 14px !important;
    }
    @media (max-width: 1999px) {
      font-size: 16px;
    }
    @media (max-width: 1599px) {
      font-size: 14px;
    }
    > .icon-w {
      @media (max-width: 991px) {
        display: none;
      }
    }
  }
  .button-text-w {
    margin-right: 9px;
    @media (max-width: 1279px) {
      margin-right: 9px;
    }
    @media (max-width: 991px) {
      margin-right: 0;
    }

    .icon-w {
      margin-right: 10px;
      @media (max-width: 1279px) {
        margin-right: 0;
      }
    }
    .text {
      max-width: 220px;
      text-overflow: ellipsis;
      overflow: hidden;
      display: block;
      @media (max-width: 1999px) {
        max-width: 160px;
      }
      @media (resolution: 120dpi) and (max-width: 1999px) {
        max-width: 140px;
      }
      
      @media (max-width: 1599px) {
        display: none;
      }
    }
  }
  .dropdown-menu {
    margin-top: 10px;
    right: auto;
    left: -28px;
    width: 264px;
    @media (max-width: 991px) {
      left: auto;
      right: -74px;
      width: 305px;
    }
    .dropdown-content {
      border: 1px solid #d3d6e2;
      padding: 0.5rem 0;
      border-radius: 4px;
      position: relative;
      box-shadow: 0px 0px 12px rgba(30, 39, 44, 0.3);
      &:before {
        content: "";
        display: block;
        position: absolute;
        left: 33px;
        top: -8px;
        border-width: 0 7px 8px;
        border-style: solid;
        border-color: transparent transparent #fff;
        z-index: 2;
        @media (max-width: 991px) {
          left: auto;
          right: 79px;
        }
      }
      &:after {
        content: "";
        display: block;
        position: absolute;
        left: 33px;
        top: -9px;
        border-width: 0 7px 8px;
        border-style: solid;
        border-color: transparent transparent rgba(0, 0, 0, 0.2);
        z-index: 1;
        @media (max-width: 991px) {
          left: auto;
          right: 79px;
        }
      }
    }
  }
  .dropdown-item {
    &.dropdown-caption {
      font-size: 0.8rem;
      font-weight: bold;

      &:hover {
        background: none;
      }
    }

    &.dropdown-item-link {
      font-size: 15px;
      padding: 8px 0;
      margin: 0 15px;
      width: auto;

      @media (max-width: 991px) {
        margin: 0;
        padding: 8px 15px;
      }
      &:hover {
        background: none;
        opacity: 0.8;
      }
    }
  }
}

.userinfo {
  direction: rtl;

  .userinfo-row {
    display: flex;
    
    & > div {
      margin: 0 0 0 .2rem;

      &:last-child {
        margin-right: 0;
      }
    }
  }
}


html[dir="ltr"] {

  .userinfo {
    direction: ltr;

    .userinfo-row {

      & > div {
        margin: 0 .2rem 0 0;

        &:last-child {
          margin-left: 0;
        }
      }
    }
  }
}

</style>
